// /admin/suscripciones-empresa/:id
// Detalle de una suscripción B2B.
// Muestra: empresa, dirección, items completos, precio, día/ventana, estado, historial de cambios.
// Acciones: pausar, reactivar, cancelar, editar precio, editar día/ventana, editar items.

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const ESTADO_META = {
  activa:    { l: 'Activa',    bg: '#DCFCE7', fg: '#16A34A' },
  pausada:   { l: 'Pausada',   bg: '#FEF3C7', fg: '#CA8A04' },
  cancelada: { l: 'Cancelada', bg: '#FEE2E2', fg: '#DC2626' },
}

const VENTANAS_LABEL = {
  manana: '🌅 Mañana 10–14',
  tarde:  '🌇 Tarde 14–18',
  noche:  '🌆 Tarde-noche 18–21',
}

const METODO_COBRO_LABEL = {
  transferencia:    '🏦 Transferencia bancaria',
  manual:           '💵 Manual (efectivo/cheque/a coordinar)',
  cuenta_corriente: '📋 Cuenta corriente (paga 30 días)',
  link_pago:        '🔗 Link de pago online',
  boleto:           '🎟 Boleto Abitab/RedPagos',
  tarjeta_auto:     '💳 Tarjeta automática (MP suscripción · cobro cada 30 días)',
  prepaid_d1:       '💳 Tarjeta automática (MP suscripción · cobro cada 30 días)', // legacy
  postpago:         '📋 Postpago',                       // legacy
}

export default function SuscripcionEmpresaDetalle() {
  const { id } = useParams()
  const nav = useNavigate()

  const [s, setS] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [accion, setAccion] = useState(null)
  const [ok, setOk] = useState(null)

  // Estado para edición inline (entrega — precio/descuento están en su propio componente)
  const [editEntrega, setEditEntrega] = useState(false)
  const [diaInput, setDiaInput] = useState('')
  const [ventanaInput, setVentanaInput] = useState('')

  async function cargar() {
    setCargando(true)
    const { data, error: e } = await supabase
      .from('suscripciones_empresa')
      .select(`
        *,
        empresa:empresas(id, razon_social, rut, tipo, facturacion_modo, contacto_principal_nombre, email_facturacion, telefono),
        direccion:empresas_direcciones(id, alias, calle, numero, apto_unidad, contacto_local_nombre, contacto_local_telefono, observaciones, barrio:barrios(nombre, dias)),
        presupuesto:presupuestos!suscripciones_empresa_presupuesto_origen_id_fkey(id, numero, fecha, total),
        owner:profiles!suscripciones_empresa_owner_id_fkey(id, nombre),
        creador:profiles!suscripciones_empresa_created_by_fkey(id, nombre)
      `)
      .eq('id', id)
      .single()
    if (e) setError(e.message)
    else {
      setS(data)
      setDiaInput(data.dia_entrega || 'lunes')
      setVentanaInput(data.ventana_horaria || 'manana')
    }
    setCargando(false)
  }
  useEffect(() => { cargar() }, [id])

  async function ejecutar(patch, mensajeOk) {
    setError(null)
    setOk(null)
    setAccion('save')
    const { error: e } = await supabase.from('suscripciones_empresa').update(patch).eq('id', id)
    setAccion(null)
    if (e) setError(e.message)
    else {
      setOk(mensajeOk)
      cargar()
      setTimeout(() => setOk(null), 2500)
    }
  }

  async function pausar() {
    const hasta = prompt('¿Hasta cuándo pausar? (YYYY-MM-DD o vacío para indefinido)', '')
    if (hasta === null) return
    const motivo = prompt('Motivo (opcional)', '') || null
    await ejecutar({
      estado: 'pausada',
      pausada_at: new Date().toISOString(),
      pausada_hasta: hasta || null,
      pausada_motivo: motivo,
    }, 'Suscripción pausada')
  }

  async function reactivar() {
    if (!confirm('Reactivar esta suscripción?')) return
    await ejecutar({
      estado: 'activa',
      pausada_at: null,
      pausada_hasta: null,
      pausada_motivo: null,
    }, 'Suscripción reactivada')
  }

  async function cancelar() {
    const motivo = prompt('Motivo de cancelación (opcional)', '')
    if (motivo === null) return
    if (!confirm('Cancelar la suscripción? Queda en historial pero no se vuelve a cobrar ni entregar.')) return
    await ejecutar({
      estado: 'cancelada',
      cancelada_at: new Date().toISOString(),
      cancelada_motivo: motivo || null,
    }, 'Suscripción cancelada')
  }

  async function guardarEntrega() {
    await ejecutar({ dia_entrega: diaInput, ventana_horaria: ventanaInput }, 'Entrega actualizada')
    setEditEntrega(false)
  }

  async function eliminar() {
    if (!confirm('Borrar DEFINITIVAMENTE esta suscripción? Esto NO es lo mismo que cancelar — se borra todo registro. Solo úsalo si fue cargada por error.')) return
    setAccion('delete')
    const { error: e } = await supabase.from('suscripciones_empresa').delete().eq('id', id)
    setAccion(null)
    if (e) { setError(e.message); return }
    nav(`/admin/empresas/${s.empresa.id}`)
  }

  if (cargando) {
    return <AdminLayout title="Suscripción B2B"><div style={{ padding: 40, textAlign: 'center', color: '#64748B' }}>Cargando…</div></AdminLayout>
  }
  if (!s) {
    return <AdminLayout title="Suscripción B2B"><div style={{ padding: 24 }}>{error || 'No encontrada'}</div></AdminLayout>
  }

  const meta = ESTADO_META[s.estado] || ESTADO_META.activa
  const items = Array.isArray(s.items) ? s.items : []
  const dirsBarrio = s.direccion?.barrio?.dias || []

  return (
    <AdminLayout title={`Suscripción ${s.alias}`}>
      <Estilos />
      <div className="sd-page">
        <button className="sd-back" onClick={() => nav(`/admin/empresas/${s.empresa.id}`)}>← Volver a {s.empresa.razon_social}</button>

        <header className="sd-head">
          <div>
            <div className="sd-meta-top">
              {s.empresa.razon_social}
              {s.empresa.rut && <span className="sd-meta-rut">RUT {s.empresa.rut}</span>}
            </div>
            <h1>{s.alias}</h1>
            <div className="sd-meta">
              <span className="sd-badge" style={{ background: meta.bg, color: meta.fg }}>{meta.l}</span>
              {s.presupuesto && (
                <span className="sd-meta-link" onClick={() => nav(`/admin/presupuestos/${s.presupuesto.id}`)}>
                  📄 Desde presupuesto {s.presupuesto.numero}
                </span>
              )}
              <span className="sd-meta-sub">Activa desde {formatFecha(s.fecha_inicio)}</span>
            </div>
          </div>
          <div className="sd-precio-big">
            ${Number(s.precio_mensual_total).toLocaleString('es-UY', { maximumFractionDigits: 2 })}
            <small>/mes ({s.moneda})</small>
          </div>
        </header>

        {ok && <div className="sd-ok">✓ {ok}</div>}
        {error && <div className="sd-error">⚠ {error}</div>}

        {/* Banner según estado */}
        {s.estado === 'pausada' && (
          <div className="sd-banner-warn">
            <strong>⏸ Suscripción pausada</strong>
            {s.pausada_hasta && <> hasta {formatFecha(s.pausada_hasta)}.</>} {s.pausada_motivo && <> Motivo: {s.pausada_motivo}.</>}
            <button className="sd-btn-link" onClick={reactivar} disabled={accion}>Reactivar →</button>
          </div>
        )}
        {s.estado === 'cancelada' && (
          <div className="sd-banner-danger">
            <strong>✕ Cancelada</strong> el {formatFecha(s.cancelada_at)}.
            {s.cancelada_motivo && <> Motivo: {s.cancelada_motivo}.</>}
          </div>
        )}

        <div className="sd-actions">
          {s.estado === 'activa' && (
            <>
              <button className="sd-btn-secondary" onClick={pausar} disabled={accion}>⏸ Pausar</button>
              <button className="sd-btn-danger" onClick={cancelar} disabled={accion}>✕ Cancelar</button>
            </>
          )}
          {s.estado === 'pausada' && (
            <button className="sd-btn-success" onClick={reactivar} disabled={accion}>▶ Reactivar</button>
          )}
          <button className="sd-btn-secondary" onClick={eliminar} disabled={accion} title="Solo si fue cargada por error">🗑 Eliminar registro</button>
        </div>

        <div className="sd-grid">
          {/* Items */}
          <section className="sd-card">
            <h3>📦 Items del servicio mensual ({items.length})</h3>
            {items.length === 0 ? (
              <p className="sd-text-muted">Sin items registrados.</p>
            ) : (() => {
              const subtotalItems = items.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0)
              const totalFinal = Number(s.precio_mensual_total) || 0
              const descPctStored = Number(s.descuento_pct) || 0
              const descMonto = subtotalItems - totalFinal
              const hayDesc = descPctStored > 0 || descMonto > 0.01
              return (
                <table className="sd-items">
                  <thead><tr><th>Descripción</th><th>Cant.</th><th>P. unit.</th><th>Desc.</th><th>Subtotal</th></tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i}>
                        <td>
                          {it.descripcion}
                          {it.tipo && <span className="sd-tipo-pill">{it.tipo}</span>}
                        </td>
                        <td className="sd-num">{Number(it.cantidad)}</td>
                        <td className="sd-num">${Number(it.precio_unitario).toLocaleString('es-UY')}</td>
                        <td className="sd-num">{Number(it.descuento_pct || 0) > 0 ? Number(it.descuento_pct).toFixed(0) + '%' : '—'}</td>
                        <td className="sd-num"><strong>${Number(it.subtotal).toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {hayDesc && (
                      <>
                        <tr className="sd-tfoot-sub">
                          <td colSpan={4} className="sd-num">Subtotal items</td>
                          <td className="sd-num">${subtotalItems.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</td>
                        </tr>
                        <tr className="sd-tfoot-desc">
                          <td colSpan={4} className="sd-num">Descuento global ({descPctStored.toFixed(2)}%)</td>
                          <td className="sd-num">− ${descMonto.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td colSpan={4} className="sd-num"><strong>Total mensual</strong></td>
                      <td className="sd-num"><strong>${totalFinal.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              )
            })()}

            {/* Edit precio + descuento */}
            <EditPrecioDescuento s={s} accion={accion} ejecutar={ejecutar} />
          </section>

          <aside className="sd-aside">
            {/* Dirección */}
            <section className="sd-card">
              <h3>📍 Dirección de entrega</h3>
              {s.direccion ? (
                <>
                  <p className="sd-text"><strong>{s.direccion.alias}</strong></p>
                  <p className="sd-text">{s.direccion.calle} {s.direccion.numero || ''}{s.direccion.apto_unidad ? ` · Apto ${s.direccion.apto_unidad}` : ''}</p>
                  {s.direccion.barrio?.nombre && <p className="sd-text-muted">{s.direccion.barrio.nombre}</p>}
                  {s.direccion.contacto_local_nombre && (
                    <p className="sd-text">👤 {s.direccion.contacto_local_nombre} {s.direccion.contacto_local_telefono && `· ${s.direccion.contacto_local_telefono}`}</p>
                  )}
                  {s.direccion.observaciones && <p className="sd-text-muted sd-italic">{s.direccion.observaciones}</p>}
                </>
              ) : (
                <p className="sd-text-muted">Sin dirección asignada.</p>
              )}
            </section>

            {/* Día/Ventana */}
            <section className="sd-card">
              <h3>🗓️ Entrega</h3>
              {!editEntrega ? (
                <>
                  <p className="sd-text">📅 <strong>{s.dia_entrega}</strong></p>
                  <p className="sd-text">⏰ {VENTANAS_LABEL[s.ventana_horaria] || s.ventana_horaria}</p>
                  {s.proxima_entrega && <p className="sd-text-muted">Próxima: {formatFecha(s.proxima_entrega)}</p>}
                  <button className="sd-btn-link" onClick={() => setEditEntrega(true)}>✏ Cambiar día / ventana</button>
                </>
              ) : (
                <div className="sd-edit-form sd-vert">
                  <label className="sd-field">
                    <span>Día</span>
                    <select value={diaInput} onChange={e => setDiaInput(e.target.value)}>
                      {['lunes','martes','miercoles','jueves','viernes','sabado'].map(d => (
                        <option key={d} value={d} disabled={dirsBarrio.length && !dirsBarrio.includes(d)}>
                          {d}{dirsBarrio.length && !dirsBarrio.includes(d) ? ' (fuera ruta)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="sd-field">
                    <span>Ventana</span>
                    <select value={ventanaInput} onChange={e => setVentanaInput(e.target.value)}>
                      {Object.entries(VENTANAS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </label>
                  <div className="sd-edit-buttons">
                    <button className="sd-btn-primary sd-btn-sm" onClick={guardarEntrega} disabled={accion}>Guardar</button>
                    <button className="sd-btn-secondary sd-btn-sm" onClick={() => setEditEntrega(false)}>Cancelar</button>
                  </div>
                </div>
              )}
            </section>

            {/* Cobro */}
            <section className="sd-card">
              <h3>💳 Método de cobro</h3>
              <p className="sd-text">{METODO_COBRO_LABEL[s.modelo_cobro] || s.modelo_cobro}</p>
              <select
                className="sd-mini-select"
                value={s.modelo_cobro}
                disabled={accion === 'save'}
                onChange={e => ejecutar({ modelo_cobro: e.target.value }, 'Método de cobro actualizado')}
              >
                <option value="transferencia">🏦 Transferencia</option>
                <option value="manual">💵 Manual (efectivo/cheque)</option>
                <option value="cuenta_corriente">📋 Cuenta corriente 30d</option>
                <option value="link_pago">🔗 Link de pago (próximamente)</option>
                <option value="boleto">🎟 Boleto Abitab (próx.)</option>
                <option value="tarjeta_auto">💳 Tarjeta auto MP suscripción (próx.)</option>
                {/* legacy: suscripciones viejas pueden tener estos valores */}
                {!['transferencia','manual','cuenta_corriente','link_pago','boleto','tarjeta_auto'].includes(s.modelo_cobro) && (
                  <option value={s.modelo_cobro}>{METODO_COBRO_LABEL[s.modelo_cobro] || s.modelo_cobro} (legacy)</option>
                )}
              </select>
              {s.ultimo_cobro_at && <p className="sd-text-muted">Último cobro: {formatFecha(s.ultimo_cobro_at)}</p>}
              {s.proximo_cobro && <p className="sd-text-muted">Próximo: {formatFecha(s.proximo_cobro)}</p>}
              {['link_pago','boleto','tarjeta_auto'].includes(s.modelo_cobro) && (
                <p className="sd-text-warn">⚠ Este método requiere integración con Handy (pendiente). Mientras tanto, cobrá manual.</p>
              )}
            </section>

            {/* Facturación */}
            <section className="sd-card">
              <h3>🧾 Facturación</h3>
              <p className="sd-text">
                Modo: {(s.facturacion_modo || s.empresa.facturacion_modo) === 'consolidada' ? 'Consolidada a razón social' : 'Una por edificio (default)'}
              </p>
              {s.ultima_factura_cfe && <p className="sd-text-muted">Última CFE: {s.ultima_factura_cfe}</p>}
              <p className="sd-text-warn">⚠ Zsoftware todavía no integrado. Facturas manuales hasta tener RUT SAS + API Zsoftware.</p>
            </section>

            {/* Notas */}
            {s.notas && (
              <section className="sd-card">
                <h3>📝 Notas</h3>
                <p className="sd-text" style={{ whiteSpace: 'pre-line' }}>{s.notas}</p>
              </section>
            )}

            {/* Metadatos */}
            <section className="sd-card sd-card-muted">
              <h3>ℹ Datos administrativos</h3>
              <p className="sd-text-muted">Creada: {formatFecha(s.created_at)}{s.creador?.nombre ? ` por ${s.creador.nombre}` : ''}</p>
              {s.owner?.nombre && <p className="sd-text-muted">Owner: {s.owner.nombre}</p>}
              <p className="sd-text-muted sd-mono">ID: {s.id}</p>
            </section>
          </aside>
        </div>
      </div>
    </AdminLayout>
  )
}

function formatFecha(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

// Editor combinado de precio mensual + descuento global.
// Vinculados: si edita precio, recalcula descuento. Si edita descuento, recalcula precio.
function EditPrecioDescuento({ s, accion, ejecutar }) {
  const items = Array.isArray(s.items) ? s.items : []
  const subtotalItems = items.reduce((acc, it) => acc + (Number(it.subtotal) || 0), 0)

  const [editando, setEditando] = useState(false)
  const [modo, setModo] = useState('precio') // 'precio' | 'descuento'
  const [precioInput, setPrecioInput] = useState(String(s.precio_mensual_total || ''))
  const [descInput, setDescInput] = useState(String(s.descuento_pct || 0))

  function reset() {
    setPrecioInput(String(s.precio_mensual_total || ''))
    setDescInput(String(s.descuento_pct || 0))
    setEditando(false)
  }

  function setPrecio(v) {
    setPrecioInput(v)
    const precio = Number(v) || 0
    if (subtotalItems > 0) {
      const desc = Math.max(0, Math.min(100, ((subtotalItems - precio) / subtotalItems) * 100))
      setDescInput(desc.toFixed(2))
    }
  }

  function setDesc(v) {
    setDescInput(v)
    const desc = Number(v) || 0
    if (subtotalItems > 0) {
      const precio = subtotalItems * (1 - desc / 100)
      setPrecioInput(precio.toFixed(2))
    }
  }

  async function guardar() {
    const precio = Number(precioInput)
    const desc = Number(descInput)
    if (!precio || precio <= 0) {
      alert('Precio inválido')
      return
    }
    if (desc < 0 || desc > 100) {
      alert('Descuento debe estar entre 0 y 100%')
      return
    }
    await ejecutar({
      precio_mensual_total: Math.round(precio * 100) / 100,
      descuento_pct: Math.round(desc * 100) / 100,
    }, 'Precio y descuento actualizados')
    setEditando(false)
  }

  if (!editando) {
    return (
      <div className="sd-edit-row">
        <button className="sd-btn-link" onClick={() => setEditando(true)}>✏ Cambiar precio / descuento</button>
        <p className="sd-text-muted" style={{ marginTop: 6, fontSize: 11.5 }}>
          💡 Podés editar el <strong>precio mensual</strong> y el <strong>descuento %</strong> en conjunto — uno se calcula del otro a partir del subtotal de items.
        </p>
      </div>
    )
  }

  return (
    <div className="sd-edit-row">
      <div className="sd-edit-pd">
        <div className="sd-edit-pd-row">
          <label>
            <span>Precio mensual (UYU c/IVA)</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={precioInput}
              onChange={e => { setModo('precio'); setPrecio(e.target.value) }}
              onFocus={() => setModo('precio')}
              autoFocus={modo === 'precio'}
            />
          </label>
          <label>
            <span>Descuento global %</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={descInput}
              onChange={e => { setModo('descuento'); setDesc(e.target.value) }}
              onFocus={() => setModo('descuento')}
            />
          </label>
        </div>
        <div className="sd-edit-pd-hint">
          Subtotal items: <strong>${subtotalItems.toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong>
          {Number(descInput) > 0 && (
            <> · Descuento: <strong>−${((subtotalItems * Number(descInput)) / 100).toLocaleString('es-UY', { maximumFractionDigits: 2 })}</strong></>
          )}
        </div>
        <div className="sd-edit-pd-actions">
          <button className="sd-btn-primary sd-btn-sm" onClick={guardar} disabled={accion}>Guardar</button>
          <button className="sd-btn-secondary sd-btn-sm" onClick={reset}>Cancelar</button>
        </div>
      </div>
    </div>
  )
}

function Estilos() {
  return (
    <style>{`
      .sd-page { max-width: 1200px; margin: 0 auto; padding: 24px; font-family: Inter, system-ui, sans-serif; color: #1A3461; }
      .sd-back { background: transparent; border: none; color: #64748B; font-size: 13px; cursor: pointer; padding: 4px 0; margin-bottom: 8px; font-family: inherit; }
      .sd-back:hover { color: #1A3461; }

      .sd-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 18px; flex-wrap: wrap; }
      .sd-meta-top { font-size: 12.5px; color: #64748B; font-weight: 600; }
      .sd-meta-rut { margin-left: 8px; font-family: 'SF Mono', monospace; font-size: 11px; }
      .sd-head h1 { margin: 4px 0 6px; font-size: 22px; font-weight: 900; letter-spacing: -0.02em; }
      .sd-meta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; font-size: 12.5px; }
      .sd-badge { padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; }
      .sd-meta-link { color: #00A8B5; cursor: pointer; }
      .sd-meta-link:hover { text-decoration: underline; }
      .sd-meta-sub { color: #64748B; }

      .sd-precio-big { font-size: 26px; font-weight: 900; color: #00A8B5; line-height: 1; font-variant-numeric: tabular-nums; }
      .sd-precio-big small { font-size: 12px; color: #64748B; font-weight: 600; margin-left: 4px; }

      .sd-ok { background: #DCFCE7; border-left: 3px solid #16A34A; color: #14532D; padding: 10px 14px; border-radius: 0 8px 8px 0; font-size: 13px; margin-bottom: 12px; }
      .sd-error { background: #FEE2E2; border-left: 3px solid #DC2626; color: #991B1B; padding: 10px 14px; border-radius: 0 8px 8px 0; font-size: 13px; margin-bottom: 12px; }
      .sd-banner-warn { background: #FEF3C7; border-left: 3px solid #F59E0B; padding: 12px 16px; border-radius: 0 8px 8px 0; color: #78350F; font-size: 13px; margin-bottom: 14px; }
      .sd-banner-danger { background: #FEE2E2; border-left: 3px solid #DC2626; padding: 12px 16px; border-radius: 0 8px 8px 0; color: #991B1B; font-size: 13px; margin-bottom: 14px; }

      .sd-actions { display: flex; gap: 8px; flex-wrap: wrap; padding: 14px; background: #F8FAFC; border-radius: 10px; border: 1px solid #E2E8F0; margin-bottom: 16px; }
      .sd-btn-primary, .sd-btn-secondary, .sd-btn-success, .sd-btn-danger, .sd-btn-link {
        padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 700;
        font-family: inherit; cursor: pointer; transition: all 0.15s; border: none;
      }
      .sd-btn-primary { background: #1A3461; color: #fff; }
      .sd-btn-primary:hover { background: #1A3461; }
      .sd-btn-primary:disabled { opacity: 0.5; cursor: wait; }
      .sd-btn-secondary { background: #fff; color: #475569; border: 1.5px solid #E2E8F0; }
      .sd-btn-secondary:hover { border-color: #1A3461; color: #1A3461; }
      .sd-btn-success { background: #16A34A; color: #fff; }
      .sd-btn-success:hover { background: #15803D; }
      .sd-btn-danger { background: #fff; color: #DC2626; border: 1.5px solid #FEE2E2; }
      .sd-btn-danger:hover { background: #FEE2E2; }
      .sd-btn-link { background: transparent; color: #00A8B5; padding: 4px 8px; font-size: 12.5px; text-decoration: underline; }
      .sd-btn-sm { padding: 6px 10px; font-size: 12px; }

      .sd-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 18px; }
      @media (max-width: 900px) { .sd-grid { grid-template-columns: 1fr; } }

      .sd-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 12px; padding: 16px 18px; margin-bottom: 14px; }
      .sd-card h3 { margin: 0 0 10px; font-size: 13px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.06em; }
      .sd-card-muted { background: #F8FAFC; }
      .sd-text { margin: 4px 0; font-size: 13.5px; color: #1A3461; }
      .sd-text-muted { font-size: 12.5px; color: #64748B; margin: 4px 0; }
      .sd-text-warn { font-size: 12.5px; color: #78350F; margin: 6px 0 0; }
      .sd-italic { font-style: italic; }
      .sd-mono { font-family: 'SF Mono', monospace; font-size: 11px; }

      .sd-items { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
      .sd-items th { background: #F8FAFC; padding: 9px 10px; font-size: 11px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: 0.05em; text-align: left; border-bottom: 1px solid #E2E8F0; }
      .sd-items td { padding: 10px; border-bottom: 1px solid #F1F5F9; font-size: 13px; vertical-align: top; }
      .sd-items tr:last-child td { border-bottom: none; }
      .sd-items tfoot tr td { background: #1A3461; color: #fff; padding: 10px; font-size: 13px; }
      .sd-items tfoot tr.sd-tfoot-sub td { background: #F8FAFC; color: #475569; padding: 8px 10px; font-size: 12.5px; border-bottom: 1px solid #E2E8F0; }
      .sd-items tfoot tr.sd-tfoot-desc td { background: #F0FDFA; color: #134E4A; padding: 8px 10px; font-size: 12.5px; font-weight: 600; border-bottom: 1px solid #E2E8F0; }
      .sd-num { text-align: right; font-variant-numeric: tabular-nums; }
      .sd-tipo-pill { background: #F1F5F9; color: #64748B; font-size: 10px; padding: 1px 6px; border-radius: 999px; margin-left: 6px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; }

      .sd-edit-row { padding-top: 10px; border-top: 1px dashed #E2E8F0; margin-top: 10px; }
      .sd-edit-form { display: flex; gap: 8px; align-items: flex-end; flex-wrap: wrap; }
      .sd-edit-form input { padding: 8px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0; font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff; width: 140px; }
      .sd-edit-form input:focus { outline: none; border-color: #00A8B5; }
      .sd-edit-form select { padding: 8px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0; font-size: 13.5px; font-family: inherit; color: #1A3461; background: #fff; }
      .sd-edit-form.sd-vert { flex-direction: column; align-items: stretch; gap: 10px; }
      .sd-edit-buttons { display: flex; gap: 6px; }
      .sd-field { display: block; }
      .sd-field > span { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
      .sd-field select { width: 100%; }

      .sd-aside { display: flex; flex-direction: column; }

      code { background: #F1F5F9; padding: 1px 5px; border-radius: 4px; font-family: 'SF Mono', monospace; font-size: 11.5px; color: #1A3461; }

      /* Editor combinado precio + descuento */
      .sd-edit-pd { background: #F8FAFC; border: 1px solid #E2E8F0; border-radius: 10px; padding: 14px; }
      .sd-edit-pd-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 10px; }
      @media (max-width: 540px) { .sd-edit-pd-row { grid-template-columns: 1fr; } }
      .sd-edit-pd-row label > span { display: block; font-size: 11px; font-weight: 700; color: #475569; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
      .sd-edit-pd-row input { width: 100%; padding: 9px 11px; border-radius: 8px; border: 1.5px solid #E2E8F0; font-size: 14px; font-family: inherit; color: #1A3461; background: #fff; box-sizing: border-box; font-variant-numeric: tabular-nums; }
      .sd-edit-pd-row input:focus { outline: none; border-color: #00A8B5; }
      .sd-edit-pd-hint { font-size: 12px; color: #64748B; padding: 8px 10px; background: #fff; border-radius: 6px; margin-bottom: 10px; }
      .sd-edit-pd-actions { display: flex; gap: 8px; justify-content: flex-end; }

      .sd-mini-select {
        width: 100%; padding: 7px 10px; border-radius: 6px; border: 1.5px solid #E2E8F0;
        font-size: 12.5px; font-family: inherit; color: #1A3461; background: #fff; margin-top: 6px; cursor: pointer;
      }
      .sd-mini-select:focus { outline: none; border-color: #00A8B5; }
    `}</style>
  )
}
